import {SystemDiagramNode} from "src/api/generated/postgres/db/types/public_/tables/systemDiagramNode";
import {
    SystemDiagramNodeCrudApi_FormInitialData
} from "src/api/generated/systemDiagrams/management/systemDiagramNodeCrudApi";
import {SystemDiagramFunction} from "src/api/generated/systemDiagrams/functions/systemDiagramFunction";
import {Typography} from "@mui/material";
import {logError} from "src/errorHandling/errorLogging";
import {Controller, UseControllerProps} from "react-hook-form";
import {
    ProductionLineSelectionField
} from "src/components/views/erp/common/systemDiagrams/functionParamForms/ProductionLineSelectionField";
import {
    OperationSelectionField
} from "src/components/views/erp/common/systemDiagrams/functionParamForms/OperationSelectionField";
import i18n from "i18next";
import {ControlChartSelectionForm} from "src/components/views/spc/common/ControlChartSelectionForm.tsx";
import {FormSerializedMultiSelectField} from "src/components/common/forms/fields/FormSerializedMultiSelectField.tsx";

export interface SystemDiagramFunctionParamsFormProps {
    control: UseControllerProps<SystemDiagramNode>["control"];
    nodeFunction: SystemDiagramFunction | undefined;
    formInitialData: SystemDiagramNodeCrudApi_FormInitialData;
}

export const SystemDiagramFunctionParamsForm = (props: SystemDiagramFunctionParamsFormProps) => {
    const {nodeFunction} = props;
    if (nodeFunction === undefined) return <></>;

    const functionType = functionTypeMap[nodeFunction.id];
    if (functionType == null) {
        logError(`System diagram node function "${nodeFunction.label}" not implemented`);
        return (
            <Typography>
                {i18n.t("function_is_not_defined", {
                    function: nodeFunction.label,
                })}
            </Typography>
        );
    }

    switch (functionType) {
        case "operations_completed_today":
            return (
                <OperationSelectionField
                    fieldName={"systemDiagramNodeFunctionParams.param1"}
                    {...props}
                />
            );

        case "first_operation_completed_any_other_not_started":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        label={i18n.t("completed_operation")}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        operationIndex={2}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param3"}
                        operationIndex={3}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param4"}
                        operationIndex={4}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param5"}
                        operationIndex={5}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param6"}
                        operationIndex={6}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param7"}
                        operationIndex={7}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param8"}
                        operationIndex={8}
                        {...props}
                    />
                </>
            );

        case "first_completed_second_started":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        operationIndex={1}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        operationIndex={2}
                        {...props}
                    />
                </>
            );

        case "first_or_second_operation_completed_third_not_started":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        operationIndex={1}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        operationIndex={2}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param3"}
                        operationIndex={3}
                        {...props}
                    />
                </>
            );

        case "first_and_second_operation_completed_third_not_started":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        operationIndex={1}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        operationIndex={2}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param3"}
                        operationIndex={3}
                        {...props}
                    />
                </>
            );

        case "operation_incomplete":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        {...props}
                    />
                </>
            );

        case "operations_started_today":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        {...props}
                    />
                    <OperationSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        {...props}
                    />
                </>
            );

        case "picked_customer_orders":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param1"}
                        productionLineIndex={1}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        productionLineIndex={2}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param3"}
                        productionLineIndex={3}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param4"}
                        productionLineIndex={4}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param5"}
                        productionLineIndex={5}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param6"}
                        productionLineIndex={6}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param7"}
                        productionLineIndex={7}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param8"}
                        productionLineIndex={8}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param9"}
                        productionLineIndex={9}
                        {...props}
                    />
                </>
            );

        case "operation_not_started_materials_available":
            return (
                <>
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param2"}
                        productionLineIndex={1}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param3"}
                        productionLineIndex={2}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param4"}
                        productionLineIndex={3}
                        {...props}
                    />
                    <ProductionLineSelectionField
                        fieldName={"systemDiagramNodeFunctionParams.param5"}
                        productionLineIndex={4}
                        {...props}
                    />
                </>
            );

        case "customer_order_in_tenant_state":
            return (
                <>
                    <FormSerializedMultiSelectField
                        label={i18n.t("state")}
                        name={"systemDiagramNodeFunctionParams.param1"}
                        control={props.control}
                        options={props.formInitialData.customerOrderTenantStateOptions}
                    />
                    <FormSerializedMultiSelectField
                        label={i18n.t("customer_order_type")}
                        name={"systemDiagramNodeFunctionParams.param2"}
                        control={props.control}
                        options={props.formInitialData.customerOrderTypeOptions.map(
                            (orderType) => ({
                                key: orderType.customerOrderTypeId,
                                label: orderType.name,
                            }),
                        )}
                    />
                </>
            );

        case "control_chart_status":
            return (
                <Controller
                    name={"systemDiagramNodeFunctionParams.param1"}
                    control={props.control}
                    render={({field}) => (
                        <ControlChartSelectionForm
                            currentControlChartId={
                                field.value == null ? undefined : parseInt(field.value)
                            }
                            onChange={field.onChange}
                        />
                    )}
                />
            );
    }
};

type FunctionType =
    | "operations_completed_today"
    | "first_operation_completed_any_other_not_started"
    | "first_completed_second_started"
    | "first_or_second_operation_completed_third_not_started"
    | "first_and_second_operation_completed_third_not_started"
    | "operation_incomplete"
    | "operations_started_today"
    | "picked_customer_orders"
    | "operation_not_started_materials_available"
    | "customer_order_in_tenant_state"
    | "control_chart_status";

const functionTypeMap: Record<number, FunctionType> = {
    1: "operations_completed_today",
    2: "first_operation_completed_any_other_not_started",
    3: "first_completed_second_started",
    4: "first_or_second_operation_completed_third_not_started",
    5: "first_and_second_operation_completed_third_not_started",
    6: "operation_incomplete",
    7: "operations_started_today",
    8: "picked_customer_orders",
    9: "operation_not_started_materials_available",
    10: "customer_order_in_tenant_state",
    100: "control_chart_status",
};
